/**
 * THE COMMERCIAL HALF of the provider ledger (`provider-outcome-1`,
 * embedded verbatim inside every `provider-outcome-2` response).
 *
 * F-OUTCOME-1 rendered the two families v2 ADDED — scope ownership and
 * attributed execution. This module renders the families v1 owns, which are
 * the ones that carry the firm's actual commercial record: was it
 * shortlisted, released to, did it quote, was it evaluated, recommended,
 * authorized — and what procurement and schedule facts sit inside the scope
 * it won.
 *
 * The contract states the boundary in the payload itself, verbatim:
 *
 *   awarded, authorized to proceed, and a procurement line opened at scope
 *   level — and past that, UNKNOWN.
 *
 * Everything here lives BELOW that line. Four rules do the work, and the
 * contract's own §8 "say this / never say this" table is the test matrix:
 *
 *  1. **AN ABSENT ROW PROVES NOTHING.** No shortlist row means UNKNOWN, not
 *     "not considered" — eligibility failures are dropped before a row is
 *     ever written. `NOT_CONSIDERED` exists in the vocabulary only so the
 *     contract can say it is never emitted, and it never reaches a screen.
 *  2. **SILENCE AND UNMATCHABILITY ARE DIFFERENT.** `NO_RESPONSE` (released,
 *     nothing came back) and `UNKNOWN` (a quote exists that names no firm we
 *     can join on — it may BE their response) must never collapse into one
 *     grey chip. Rendering the second as the first is a fabricated negative
 *     about a firm that did respond.
 *  3. **COUNTS ARE COUNTS.** Four row counts, never a funnel and never a
 *     rate. Nothing here divides one by another.
 *  4. **A DATE COMPARISON IS NOT A DELAY.** Both dates are the tenant's own
 *     plan, overwritten in place. "Delivered N days after the plan as it
 *     stands today" is what the payload says; "N days late" is a verdict it
 *     never states.
 */

export const COMMERCIAL_LEDGER_CONTRACT = 'provider-outcome-1';

/**
 * The absence word the contract emits so it can promise never to emit it.
 * If this string ever reaches a rendered state the surface is wrong, and a
 * test asserts it does not.
 */
export const NEVER_EMITTED_ABSENCE = 'NOT_CONSIDERED';

/* -------------------------------------------------------------------------
 * ATTRIBUTION STRENGTH — five, ordered, and only the first names a provider
 * downstream of a quote
 * ---------------------------------------------------------------------- */

const ATTRIBUTION_STRENGTHS = {
  'quote-level': {
    key: 'quote-level',
    label: 'Quote-level',
    rank: 1,
    tone: 'strong',
    meaning:
      'A quote naming a confirmed record of this firm, and everything reached through the award proposal’s own recommended quote. The strongest attribution this platform has.',
    qualifier: null,
  },
  'scope-level': {
    key: 'scope-level',
    label: 'Scope-level',
    rank: 2,
    tone: 'moderate',
    meaning:
      'A line opened inside the scope this firm was awarded. It never says what the firm promised, shipped, or did.',
    qualifier: 'inside their awarded scope',
  },
  'human-asserted': {
    key: 'human-asserted',
    label: 'A person in this tenant said so',
    rank: 3,
    tone: 'asserted',
    meaning: 'Someone recorded this. It stays asserted — it is never promoted.',
    qualifier: null,
  },
  inferred: {
    key: 'inferred',
    label: 'Inferred from a row several doors write',
    rank: 4,
    tone: 'weak',
    meaning:
      'A row exists that more than one code path writes with no commercial response behind it. Never a headline.',
    qualifier: null,
  },
  none: {
    key: 'none',
    label: 'Attached to no provider record',
    rank: 5,
    tone: 'none',
    meaning:
      'This ledger cannot attach the fact to any provider record, including this one.',
    qualifier: null,
  },
};

export const attributionDisplay = (attribution) =>
  ATTRIBUTION_STRENGTHS[attribution] || {
    key: String(attribution ?? 'none'),
    label: 'A strength this view does not name',
    rank: 99,
    tone: 'none',
    meaning:
      'The server named an attribution strength this view does not carry. It is shown verbatim rather than mapped onto one that looks close.',
    qualifier: null,
  };

/** `inferred` and `none` may never be a headline — the contract says so. */
export const canHeadline = (attribution) => attributionDisplay(attribution).rank <= 3;

/* -------------------------------------------------------------------------
 * ABSENCE — four kinds that must never collapse
 * ---------------------------------------------------------------------- */

const ABSENCE_KINDS = {
  UNKNOWN: {
    key: 'UNKNOWN',
    label: 'No record can carry this',
    tone: 'unknown',
  },
  NO_RESPONSE: {
    key: 'NO_RESPONSE',
    // NOT "no response" and NOT "declined": the release went out and nothing
    // has come back. That is a fact about this tenant's records, not about
    // the firm's conduct.
    label: 'Released — nothing back yet',
    tone: 'unknown',
  },
  NOT_ATTRIBUTABLE: {
    key: 'NOT_ATTRIBUTABLE',
    label: 'A record exists but names no provider defensibly',
    tone: 'unknown',
  },
  NOT_CONSIDERED: {
    key: 'NOT_CONSIDERED',
    label: 'NEVER EMITTED',
    tone: 'unknown',
  },
};

export const absenceDisplay = (absence) =>
  ABSENCE_KINDS[absence] || { key: String(absence ?? 'UNKNOWN'), label: 'Unknown', tone: 'unknown' };

/* -------------------------------------------------------------------------
 * IDENTITY — the bidder-key lineage, named and never reconciled
 * ---------------------------------------------------------------------- */

export const MULTIPLE_KEYS_NOTE =
  'This firm’s history is recorded under more than one bidder key. They are listed side by side with today’s resolution — never merged into one history, and never split into two firms.';

export const TIEBREAK_NOTE =
  'Several confirmed records of one type existed and an arbitrary-but-stable choice was made. A human may want to correct it.';

/**
 * The identity block, minus what the passport already shows.
 *
 * The confirmed/unconfirmed link lists are already on this page from
 * `provider-passport-1`; re-rendering them here would put two reads of the
 * same links side by side. What this carries is what only the ledger knows:
 * which key it would mint now, every key the firm's records have ever
 * carried, and whether an arbitrary tie-break chose today's answer.
 */
export const presentLedgerIdentity = (identity) => {
  if (!identity) return null;
  const resolution = identity.resolution || {};
  const keys = identity.historicalBidderKeys || [];
  return {
    basis: identity.basis || null,
    attribution: attributionDisplay(identity.attribution),
    asOf: identity.asOf || null,
    todaysBidderKey: resolution.todaysBidderKey || null,
    // Always false by contract. Surfaced so a reader knows the resolution is
    // recomputed per request and must not be cached against an id.
    stored: resolution.stored === true,
    tiebreak: resolution.tiebreak || null,
    tiebreakNote: resolution.tiebreak ? TIEBREAK_NOTE : null,
    typedRecordKeys: resolution.typedRecordKeys || [],
    historicalBidderKeys: keys.map((entry) => ({
      bidderKey: entry.bidderKey,
      isTodaysResolution: entry.isTodaysResolution === true,
      namedBy: entry.namedBy || [],
      namedByTruncated: entry.namedByTruncated === true,
      // The literal string "UNKNOWN" is a legitimate value here for a key
      // that no release or quote ever carried.
      firstSeenAt: entry.firstSeenAt || null,
      lastSeenAt: entry.lastSeenAt || null,
      attribution: attributionDisplay(entry.attribution),
    })),
    multipleKeys: keys.length > 1,
    multipleKeysNote: keys.length > 1 ? MULTIPLE_KEYS_NOTE : null,
    unconfirmedCount: (identity.unconfirmedLinks || []).length,
    unconfirmedNote: identity.unconfirmedNote || null,
    evidenceTruncated: identity.evidenceTruncated === true,
  };
};

/* -------------------------------------------------------------------------
 * COMMERCIAL HISTORY
 * ---------------------------------------------------------------------- */

export const CONSIDERED_UNKNOWN_HEADLINE = 'No shortlist record';

export const CONSIDERED_UNKNOWN_DETAIL =
  'A shortlist row proves consideration; its absence proves nothing. Candidates that fail eligibility are dropped before any row is written, and the proposer only runs when a person clicks it.';

/**
 * "Was this firm considered?" has exactly two answers, and the second is not
 * a negative.
 */
export const presentConsidered = (considered) => {
  if (!considered) return null;
  const isConsidered = considered.value === 'CONSIDERED';
  return {
    value: considered.value || 'UNKNOWN',
    considered: isConsidered,
    headline: isConsidered ? 'Shortlisted' : CONSIDERED_UNKNOWN_HEADLINE,
    detail: isConsidered ? null : considered.basis || CONSIDERED_UNKNOWN_DETAIL,
    shortlistRowCount: considered.shortlistRowCount ?? 0,
    // The family label reads human-asserted whenever ANY row exists, even one
    // automation proposed and nobody decided. The honest per-row label lives
    // on the temporal step, so this one is carried but flagged.
    attribution: attributionDisplay(considered.attribution),
    attributionCaveat: isConsidered
      ? 'This is the family’s label. When attributing a shortlist fact to a person, read the step in the record list below — it carries whether a human decided the row or automation proposed it.'
      : null,
  };
};

const RESPONSE_ANSWERS = {
  RESPONDED: {
    key: 'RESPONDED',
    label: 'Quoted',
    tone: 'strong',
    meaning: 'A quote on this package names a confirmed record of this firm, or carries this release’s own bidder key.',
  },
  NO_RESPONSE: {
    key: 'NO_RESPONSE',
    label: 'Released — nothing back yet',
    tone: 'unknown',
    meaning: 'The release was authorized and no submitted quote on its package is this firm’s.',
  },
  UNKNOWN: {
    key: 'UNKNOWN',
    // Deliberately a different chip from NO_RESPONSE. A quote sits on this
    // package that names no firm this ledger can join on — it may BE this
    // firm's response. Rendering that as silence would be a fabricated
    // negative about a provider who answered.
    label: 'Released — a quote here could not be matched to a firm',
    tone: 'conflict',
    meaning: 'One of the quotes on this package may be this firm’s response. The ledger declines to call this silence.',
  },
};

export const responseDisplay = (value) =>
  RESPONSE_ANSWERS[value] || {
    key: String(value ?? 'UNKNOWN'),
    label: 'An answer this view does not name',
    tone: 'unknown',
    meaning: 'The server named a response value this view does not carry.',
  };

export const RELEASE_VERBS_NOTE =
  'Nothing on this surface sends anything. “Prepared” and “released for RFQ” describe internal records — not an email, an invitation, or a notification.';

export const presentRelease = (release) => {
  if (!release) return null;
  const response = release.response || {};
  return {
    recordId: release.recordId,
    packageId: release.packageId || null,
    // The literal string "UNKNOWN" when the release's own scope snapshot
    // carries no project.
    projectId: release.projectId || null,
    state: release.state || null,
    released: release.state === 'RELEASED',
    bidderKey: release.bidderKey || null,
    preparedBy: release.preparedBy || null,
    preparedAt: release.preparedAt || null,
    releasedBy: release.releasedBy || null,
    releasedAt: release.releasedAt || null,
    ageDays: release.ageDays ?? null,
    response: {
      ...responseDisplay(response.value),
      value: response.value || 'UNKNOWN',
      absence: response.absence || null,
      attribution: attributionDisplay(response.attribution),
      basis: response.basis || null,
      evidence: response.evidence || [],
    },
    // Constants repeated from exchange-rfq-1, carried so the UI cannot drift
    // into "sent".
    rfqSent: release.rfqSent === true,
    providerNotified: release.providerNotified === true,
  };
};

export const COUNTS_NOT_A_FUNNEL_NOTE =
  'Four counts of rows. Not a funnel and not a rate: nothing here divides one by another, and no ratio may be read off them.';

/**
 * Per-package authorized quote — the passport composer's three shapes.
 * `winner` exists only in the CONFLICT shape and is always null.
 */
export const presentAuthorizedQuote = (entry) => {
  if (entry === undefined || entry === null) {
    return { shape: 'NO_KEY', headline: 'No authorization recorded for this package', candidates: [], sources: [] };
  }
  if (entry.value === 'CONFLICT') {
    return {
      shape: 'CONFLICT',
      headline: 'Records disagree on which quote was authorized',
      candidates: entry.candidates || [],
      winner: null,
      sources: [],
    };
  }
  if (entry.value === 'UNKNOWN') {
    return {
      shape: 'UNKNOWN',
      headline: 'Nothing states which quote was authorized',
      absence: entry.absence || 'UNKNOWN',
      candidates: [],
      sources: entry.sources || [],
    };
  }
  return {
    shape: 'STATED',
    headline: 'Authorized quote',
    quoteId: entry.value || null,
    candidates: [],
    sources: entry.sources || [],
  };
};

export const presentCommercialHistory = (family) => {
  if (!family) return null;
  const byPackage = family.authorizedQuoteByPackage || {};
  return {
    considered: presentConsidered(family.considered),
    releases: (family.releases || []).map(presentRelease).filter(Boolean),
    releaseVerbsNote: RELEASE_VERBS_NOTE,
    counts: [
      { key: 'quoteCount', label: 'Quotes attributable to this firm', value: family.quoteCount ?? 0 },
      { key: 'evaluatedQuoteCount', label: 'Of those, evaluated', value: family.evaluatedQuoteCount ?? 0 },
      { key: 'recommendationCount', label: 'Live proposals recommending one', value: family.recommendationCount ?? 0 },
      { key: 'authorizedToProceedCount', label: 'Authorized to proceed', value: family.authorizedToProceedCount ?? 0 },
    ],
    countsNote: COUNTS_NOT_A_FUNNEL_NOTE,
    authorizedByPackage: Object.keys(byPackage)
      .sort()
      .map((packageId) => ({ packageId, ...presentAuthorizedQuote(byPackage[packageId]) })),
    ceilingReached: family.attributionCeilingReached === true,
    unresolvedAwardChains: (family.unresolvedAwardChains || []).map(presentUnresolvedChain).filter(Boolean),
    unresolvedQuotesOnReleasedPackages: (family.unresolvedQuotesOnReleasedPackages || [])
      .map(presentUnresolvedChain)
      .filter(Boolean),
    packageAwardFlags: (family.packageAwardFlags || []).map(presentAwardFlag).filter(Boolean),
  };
};

export const NOT_ATTRIBUTABLE_NOTE =
  'Every identity reference on the quote this chain rests on is empty, so the chain names no provider at all — including this one.';

export const UNRESOLVED_INTEREST_NOTE =
  'The quote names only a marketplace interest, which this ledger does not dereference. Whose response it is cannot be decided here — that is not the same as “not this firm’s”.';

export const presentUnresolvedChain = (chain) => {
  if (!chain) return null;
  const notAttributable = chain.absence === 'NOT_ATTRIBUTABLE';
  return {
    recordType: chain.recordType || null,
    recordId: chain.recordId || null,
    packageId: chain.packageId || null,
    state: chain.state || null,
    recommendedQuoteId: chain.recommendedQuoteId || null,
    bidderKey: chain.bidderKey || null,
    absence: chain.absence || 'UNKNOWN',
    absenceDisplay: absenceDisplay(chain.absence),
    // The two must not collapse: one belongs to nobody, the other is simply
    // undecidable here.
    headline: notAttributable
      ? 'This record names no provider we can identify'
      : 'Whose response this is cannot be decided here',
    detail: chain.basis || (notAttributable ? NOT_ATTRIBUTABLE_NOTE : UNRESOLVED_INTEREST_NOTE),
    attribution: attributionDisplay(chain.attribution),
  };
};

export const AWARD_FLAG_UNCORROBORATED_HEADLINE =
  'Flagged as awarded to this firm — no quote or proposal behind the flag';

export const presentAwardFlag = (flag) => {
  if (!flag) return null;
  const corroborated = flag.corroboratedByQuoteLevel === true;
  return {
    recordId: flag.recordId || null,
    projectId: flag.projectId || null,
    status: flag.status || null,
    vendorId: flag.vendorId || null,
    awardedAt: flag.awardedAt || null,
    ageDays: flag.ageDays ?? null,
    // Always `inferred` on this family — several routes write the
    // status/vendor pair with no quote and no proposal behind it.
    attribution: attributionDisplay(flag.attribution),
    corroborated,
    headline: corroborated
      ? 'Flagged as awarded, corroborated by a quote-level record'
      : AWARD_FLAG_UNCORROBORATED_HEADLINE,
    absence: flag.absence || null,
    absenceDisplay: flag.absence ? absenceDisplay(flag.absence) : null,
    detail: flag.basis || null,
    // v2 adds these two on every flag; carried so a scope assignment is
    // never mistaken for the live commercial boolean.
    scopeAssignment: flag.corroboratedByScopeAssignment || null,
    scopeAssignmentEvidence: flag.scopeAssignmentEvidence || [],
  };
};

/* -------------------------------------------------------------------------
 * PROCUREMENT OUTCOMES — scope-level, and only scope-level
 * ---------------------------------------------------------------------- */

export const SCOPE_MEMBERSHIP_LABEL = 'inside their awarded scope';

export const SUBCONTRACTOR_ONLY_NOTE =
  'Procurement outcomes are not recorded for a firm this tenant holds only as a subcontractor company — the procurement table carries no column for one. This is an absence, not a zero, and it says nothing about what this firm was awarded.';

export const presentProcurementLine = (line) => {
  if (!line) return null;
  return {
    recordId: line.recordId || null,
    projectId: line.projectId || null,
    packageId: line.packageId || null,
    name: line.name || null,
    status: line.status || null,
    quantity: line.quantity ?? null,
    unit: line.unit || null,
    // A shape check (quantity 1, unit LS), never a quality signal.
    lumpSumAwardLineShape: line.lumpSumAwardLineShape === true,
    orderDate: line.orderDate || null,
    expectedDelivery: line.expectedDelivery || null,
    actualDelivery: line.actualDelivery || null,
    ageDays: line.ageDays ?? null,
    attribution: attributionDisplay(line.attribution),
    // The boolean the contract says to check before writing "their scope".
    corroboratedByCommercialRecord: line.corroboratedByCommercialRecord === true,
    corroboratedByScopeAssignment: line.corroboratedByScopeAssignment || null,
    evidenceStatus: line.evidenceStatus || null,
    scopeMembership: line.scopeMembership || [],
    scopeMembershipTruncated: line.scopeMembershipTruncated === true,
    scopeMembershipBasis: line.scopeMembershipBasis || null,
    scopeMembershipLabel: SCOPE_MEMBERSHIP_LABEL,
    provenance: line.provenance || null,
  };
};

export const presentProcurementOutcomes = (family) => {
  if (!family) return null;
  const lines = (family.lines || []).map(presentProcurementLine).filter(Boolean);
  return {
    basis: family.basis || null,
    scopeLevelOnly: family.scopeLevelOnly === true,
    attribution: attributionDisplay(family.attribution),
    lineCount: family.lineCount ?? lines.length,
    lines,
    linesTruncated: family.linesTruncated === true,
    caveats: family.caveats || [],
    // A feature, not an omission: three columns with database defaults on a
    // table nothing writes. Reading them would assert "not late", "not
    // blocking", "not sworn in".
    withheldColumns: family.withheldColumns || [],
    // Zero lines is absent, never "no work awarded".
    empty: (family.lineCount ?? lines.length) === 0,
    emptyNote: (family.lineCount ?? lines.length) === 0 ? SUBCONTRACTOR_ONLY_NOTE : null,
  };
};

/* -------------------------------------------------------------------------
 * SCHEDULE OBSERVATIONS — correlation, and nothing beyond it
 * ---------------------------------------------------------------------- */

export const PLAN_OVERWRITTEN_NOTE =
  'Both dates are this tenant’s own plan on its own commercial line, overwritten in place — never a commitment this provider made. Any difference computed from them under-reports by exactly the slip already absorbed into re-promising, and it names no cause.';

/**
 * The relation between two dates, stated as a relation.
 *
 * `daysAfterCurrentPlan` may be negative (early). The wording never says
 * "late", never says "delay", and never attaches either to the firm.
 */
export const presentScheduleObservation = (observation) => {
  if (!observation) return null;
  const days = observation.daysAfterCurrentPlan ?? null;
  const after = observation.deliveredAfterCurrentPlan === true;
  return {
    recordId: observation.recordId || null,
    projectId: observation.projectId || null,
    packageId: observation.packageId || null,
    kind: observation.kind || 'CORRELATION',
    expectedDelivery: observation.expectedDelivery || null,
    actualDelivery: observation.actualDelivery || null,
    after,
    days,
    relation:
      days === null
        ? after
          ? 'Arrived after the plan as it stands today'
          : 'Arrived on or before the plan as it stands today'
        : days > 0
          ? `Arrived ${days} day${days === 1 ? '' : 's'} after the plan as it stands today`
          : days < 0
            ? `Arrived ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} before the plan as it stands today`
            : 'Arrived on the plan as it stands today',
    observedAt: observation.observedAt || null,
    ageDays: observation.ageDays ?? null,
    attribution: attributionDisplay(observation.attribution),
    evidenceStatus: observation.evidenceStatus || null,
    // The literal string, on every observation, forever.
    isProviderDelay: observation.isProviderDelay || 'NOT_ESTABLISHED',
    scopeMembership: observation.scopeMembership || [],
    caveat: observation.caveat || PLAN_OVERWRITTEN_NOTE,
  };
};

export const NO_OBSERVATIONS_NOTE =
  'An observation exists only where a line carries both an expected and an actual delivery date. None here means the comparison cannot be made — it is not a statement that deliveries met the plan, and not a statement that they missed it.';

export const presentScheduleObservations = (family) => {
  if (!family) return null;
  const observations = (family.observations || []).map(presentScheduleObservation).filter(Boolean);
  return {
    kind: family.kind || 'CORRELATION',
    basis: family.basis || null,
    attribution: attributionDisplay(family.attribution),
    observationCount: family.observationCount ?? observations.length,
    observations,
    observationsTruncated: family.observationsTruncated === true,
    empty: observations.length === 0,
    emptyNote: observations.length === 0 ? NO_OBSERVATIONS_NOTE : null,
    // Empty by construction on every response of this contract version. It
    // is NOT a list that might fill up, and it is never "0 delays".
    delayFindingsEmpty: (family.delayFindings || []).length === 0,
    delayFindingsBasis: family.delayFindingsBasis || null,
  };
};

/* -------------------------------------------------------------------------
 * QUALITY — a human said so, or nobody did
 * ---------------------------------------------------------------------- */

export const QUALITY_UNKNOWN_HEADLINE = 'Nobody in this tenant has recorded a performance note';

export const presentQualityObservations = (family) => {
  if (!family) return null;
  const asserted = family.value === 'HUMAN_ASSERTED';
  return {
    value: family.value || 'UNKNOWN',
    asserted,
    headline: asserted ? 'Recorded by people in this tenant' : QUALITY_UNKNOWN_HEADLINE,
    absence: family.absence || 'UNKNOWN',
    attribution: attributionDisplay(family.attribution),
    neverDerived: family.neverDerived === true,
    basis: family.basis || null,
    statementCount: family.statementCount ?? 0,
    statementsTruncated: family.statementsTruncated === true,
    // A promise that binds the client: these four are self-reported, NULL
    // everywhere, and have no write surface. The UI must not fetch them from
    // another endpoint to fill the gap.
    neverRead: family.neverRead || [],
    neverReadBasis: family.neverReadBasis || null,
  };
};

/* -------------------------------------------------------------------------
 * TEMPORAL SEQUENCE — dated records with the arrows undrawn
 * ---------------------------------------------------------------------- */

const STEP_LABELS = {
  SHORTLIST_PROPOSED: 'Shortlist row proposed',
  SHORTLIST_DECIDED: 'Shortlist decided',
  RFQ_RELEASE_PREPARED: 'RFQ release prepared',
  RFQ_RELEASE_AUTHORIZED: 'Released for RFQ',
  QUOTE_RECORDED: 'Quote recorded',
  QUOTE_EVALUATED: 'Quote evaluated',
  AWARD_RECOMMENDED: 'Award recommended',
  AUTHORIZED_TO_PROCEED: 'Authorized to proceed',
  PROCUREMENT_LINE_OPENED: 'Procurement line opened',
  HUMAN_STATEMENT: 'A person recorded a statement',
  SCOPE_OWNERSHIP_RECORDED: 'Scope ownership recorded',
  SCOPE_OWNERSHIP_CLOSED: 'Scope ownership window ended',
  EXECUTION_FACT_OBSERVED: 'Execution fact observed',
};

export const stepLabel = (step) => STEP_LABELS[step] || String(step ?? 'Recorded row');

const ACTOR_KINDS = {
  human: 'a person',
  automation: 'automation',
  UNKNOWN: 'no recorded writer',
};

export const ACTOR_ID_CAVEAT =
  'The user id says whose session wrote the row; the kind says what made it. Automation running under a person’s session is still automation.';

export const presentSequenceStep = (step) => {
  if (!step) return null;
  const kind = step.actor?.kind || 'UNKNOWN';
  return {
    step: step.step || null,
    label: stepLabel(step.step),
    value: step.value ?? null,
    basis: step.basis || null,
    sourceType: step.sourceType || null,
    sourceId: step.sourceId || null,
    projectId: step.projectId || null,
    packageId: step.packageId || null,
    observedAt: step.observedAt || null,
    ageDays: step.ageDays ?? null,
    actorId: step.actor?.userId ?? null,
    actorKind: kind,
    actorLabel: ACTOR_KINDS[kind] || String(kind),
    // Only meaningful where an id and an automation kind coexist.
    actorCaveat: kind === 'automation' && step.actor?.userId ? ACTOR_ID_CAVEAT : null,
    provenance: step.provenance || null,
    attribution: attributionDisplay(step.attribution),
    evidence: step.evidence || [],
    evidenceStatus: step.evidenceStatus || null,
    isCeiling: step.isCeiling === true,
    responded: step.responded,
    placedInSequence: step.placedInSequence !== false,
  };
};

export const ARROWS_NOTE =
  'A list of dated records, not a flow. No record here is the cause, consequence, or trigger of another, and none is numbered: within a single day the order is not a chronology.';

export const UNDATED_NOTE =
  'These records carry no recorded date, so they cannot be placed in time. They are listed apart rather than defaulted to today.';

export const presentTemporalSequence = (family) => {
  if (!family) return null;
  return {
    basis: family.basis || null,
    // Always false. The moment this reads true the layout would have to
    // change, so it is carried rather than assumed.
    arrowsDrawn: family.arrowsDrawn === true,
    arrowsNote: family.arrowsNote || ARROWS_NOTE,
    count: family.count ?? (family.steps || []).length,
    steps: (family.steps || []).map(presentSequenceStep).filter(Boolean),
    stepsTruncated: family.stepsTruncated === true,
    undatedCount: family.undatedCount ?? (family.undatedSteps || []).length,
    undatedSteps: (family.undatedSteps || []).map(presentSequenceStep).filter(Boolean),
    undatedStepsTruncated: family.undatedStepsTruncated === true,
    undatedNote: family.undatedNote || UNDATED_NOTE,
  };
};

/* -------------------------------------------------------------------------
 * RECENCY — exposed, never decayed
 * ---------------------------------------------------------------------- */

export const DECAY_NOTE =
  'Age is exposed so a person can weigh it. No decay, weighting or half-life is applied here, and none may be applied downstream.';

export const presentRecency = (family) => {
  if (!family) return null;
  return {
    basis: family.basis || null,
    asOf: family.asOf || null,
    decayApplied: family.decayApplied === true,
    decayNote: family.decayNote || DECAY_NOTE,
    oldestObservedAt: family.oldestObservedAt || null,
    newestObservedAt: family.newestObservedAt || null,
  };
};

/* -------------------------------------------------------------------------
 * EXECUTION HISTORY — the section that must not be faked or hidden
 * ---------------------------------------------------------------------- */

const WRITER_STATUS = {
  TEST_ONLY_WRITER: 'A writer exists but only tests reach it',
  NO_WRITER: 'No writer at all',
  NO_PROVIDER_REFERENCE: 'The table carries no provider column',
  NO_TABLE: 'No such table exists in this schema',
  NO_PRODUCTION_CONSTRUCTOR: 'No production code constructs these rows',
};

export const writerStatusLabel = (status) =>
  WRITER_STATUS[status] || String(status ?? 'Unknown');

export const EXECUTION_HISTORY_HEADLINE =
  'OMNΛS has no writer for these execution facts — nothing can answer this';

export const EXECUTION_HISTORY_SAME_FOR_ALL =
  'This block is identical for every provider on every tenant. It says what this platform cannot instrument, so it singles nobody out — a firm with fifty finished packages gets the same answer.';

/**
 * Structurally unknown, and it must be RENDERED.
 *
 * The contract is explicit that hiding this is the worst option: a reader who
 * cannot see it assumes the commercial half is the whole picture. Equally
 * forbidden are an empty chart, a 0% bar, a grey "no data" placeholder, or a
 * "coming soon" badge — each of those reads as "this firm did nothing".
 */
export const presentExecutionHistory = (family) => {
  if (!family) return null;
  return {
    value: family.value || 'UNKNOWN',
    absence: family.absence || 'UNKNOWN',
    attribution: attributionDisplay(family.attribution),
    structural: family.structural === true,
    causality: family.causality || 'NOT_ESTABLISHED',
    headline: EXECUTION_HISTORY_HEADLINE,
    sameForAll: EXECUTION_HISTORY_SAME_FOR_ALL,
    basis: family.basis || null,
    note: family.note || null,
    missingWriters: (family.missingWriters || []).map((entry) => ({
      fact: entry.fact || null,
      column: entry.column || null,
      writer: entry.writer || null,
      status: entry.status || null,
      statusLabel: writerStatusLabel(entry.status),
      detail: entry.detail || null,
    })),
  };
};

/* -------------------------------------------------------------------------
 * THE WHOLE COMMERCIAL HALF
 * ---------------------------------------------------------------------- */

export const HUMAN_JUDGMENT_DEFERRED_NOTE =
  'Human statements about this firm are shown above under the working relationship. This ledger embeds that same canonical block verbatim and does not recompute it, so it is not repeated here.';

export const CEILING_NOT_A_BADGE_NOTE =
  'This says how far the evidence reaches. It is a boundary on what this platform can prove, not a judgement about the firm’s work — past this line the answer is unknown for every provider.';

export const presentCommercialLedger = (payload) => {
  if (!payload) return null;
  const ceiling = payload.attributionCeiling || {};
  return {
    contractVersion: payload.contractVersion || null,
    ceiling: {
      statement: ceiling.statement || null,
      hinge: ceiling.hinge || null,
      hingeNote: ceiling.hingeNote || null,
      identitylessQuoteRule: ceiling.identitylessQuoteRule || null,
      strengths: ceiling.attributionStrengths || [],
      reached: ceiling.reached === true,
      notABadge: CEILING_NOT_A_BADGE_NOTE,
    },
    identity: presentLedgerIdentity(payload.identity),
    commercialHistory: presentCommercialHistory(payload.commercialHistory),
    procurement: presentProcurementOutcomes(payload.procurementOutcomes),
    schedule: presentScheduleObservations(payload.scheduleObservations),
    quality: presentQualityObservations(payload.qualityObservations),
    sequence: presentTemporalSequence(payload.temporalSequence),
    recency: presentRecency(payload.recency),
    executionHistory: presentExecutionHistory(payload.executionHistory),
    humanJudgmentDeferred: HUMAN_JUDGMENT_DEFERRED_NOTE,
    humanJudgmentCount: payload.humanJudgment?.statementCount ?? 0,
    humanJudgmentTruncated: payload.humanJudgment?.statementsTruncated === true,
  };
};

/* -------------------------------------------------------------------------
 * THE COPY THIS MODULE AUTHORS, and the guard that walks it
 * ---------------------------------------------------------------------- */

/**
 * Drawn from the contract's own "never say this" column. Server text is
 * echoed verbatim and excluded from the sweep — we do not rewrite the
 * server's words, we only never add our own.
 */
export const FORBIDDEN_COMMERCIAL_WORDS = {
  fabricatedNegative: [
    'not considered',
    'rejected',
    'passed over',
    'ignored',
    'non-responsive',
    'declined',
    'no response',
  ],
  verdict: ['late', 'delay', 'delayed', 'slipped', 'their fault', 'clean record'],
  evaluative: ['score', 'rating', 'rank', 'star', 'grade', 'win rate', 'proven performer'],
  overclaim: ['won', 'delivered the job', 'performed', 'their material', 'their delivery'],
};

/**
 * Headings the panel renders. They live here, not inline in JSX, so the
 * forbidden-word guard walks them — a heading typed straight into the
 * component would slip past it.
 */
export const COMMERCIAL_COPY = {
  sectionTitle: 'The commercial record',
  sectionLead:
    'What this tenant recorded about this firm up to the furthest point it can evidence, and how defensibly each fact attaches to them.',
  ceilingTitle: 'How far the evidence reaches',
  identityTitle: 'The keys this firm’s history is recorded under',
  consideredTitle: 'Shortlist',
  countsTitle: 'Rows recorded',
  releasesTitle: 'RFQ releases, and what came back',
  authorizedTitle: 'Authorized quote, by package',
  unresolvedTitle: 'Records that name nobody',
  flagsTitle: 'Packages flagged as awarded',
  procurementTitle: 'Procurement lines inside the awarded scope',
  withheldTitle: 'Columns this ledger will not read',
  withheldLead:
    'Each of these has a database default on a table nothing writes. Reading one would state a favourable answer about a fact nobody recorded.',
  scheduleTitle: 'Delivery dates, compared',
  scheduleLead:
    'The arithmetic difference between two of this tenant’s own dates on a line inside the awarded scope. Same package, same project, ordered by time.',
  qualityTitle: 'Performance notes',
  neverReadTitle: 'Sources this ledger promises never to read',
  listCutShort:
    'This list is cut short. The count beside it is a floor, not a total, and nothing was dropped quietly.',
  statementFloor:
    'The statement listing behind this count was cut short, so this number is a floor rather than a total.',
  executionTitle: 'What happened on site',
  sequenceTitle: 'The records, in date order',
  undatedTitle: 'Records with no recorded date',
  recencyTitle: 'Age',
  humanJudgmentTitle: 'Human statements',
};

export const authoredCommercialCopy = () => [
  ...Object.values(COMMERCIAL_COPY),
  ...Object.values(ATTRIBUTION_STRENGTHS).flatMap((entry) => [entry.label, entry.meaning]),
  ...Object.values(ABSENCE_KINDS).map((entry) => entry.label),
  ...Object.values(RESPONSE_ANSWERS).flatMap((entry) => [entry.label, entry.meaning]),
  ...Object.values(STEP_LABELS),
  ...Object.values(WRITER_STATUS),
  ...Object.values(ACTOR_KINDS),
  CONSIDERED_UNKNOWN_HEADLINE,
  CONSIDERED_UNKNOWN_DETAIL,
  MULTIPLE_KEYS_NOTE,
  TIEBREAK_NOTE,
  RELEASE_VERBS_NOTE,
  COUNTS_NOT_A_FUNNEL_NOTE,
  NOT_ATTRIBUTABLE_NOTE,
  UNRESOLVED_INTEREST_NOTE,
  AWARD_FLAG_UNCORROBORATED_HEADLINE,
  SCOPE_MEMBERSHIP_LABEL,
  SUBCONTRACTOR_ONLY_NOTE,
  PLAN_OVERWRITTEN_NOTE,
  NO_OBSERVATIONS_NOTE,
  QUALITY_UNKNOWN_HEADLINE,
  ARROWS_NOTE,
  UNDATED_NOTE,
  DECAY_NOTE,
  ACTOR_ID_CAVEAT,
  EXECUTION_HISTORY_HEADLINE,
  EXECUTION_HISTORY_SAME_FOR_ALL,
  HUMAN_JUDGMENT_DEFERRED_NOTE,
  CEILING_NOT_A_BADGE_NOTE,
];

/** Word-boundary matching, so "related" never trips the "late" ban. */
export const findForbiddenCommercialWords = (text) => {
  if (typeof text !== 'string') return [];
  const hits = [];
  Object.values(FORBIDDEN_COMMERCIAL_WORDS).forEach((group) => {
    group.forEach((word) => {
      const pattern = new RegExp(`\\b${word.replace(/ /g, '\\s+')}\\b`, 'i');
      if (pattern.test(text)) hits.push(word);
    });
  });
  return hits;
};
